
import { Component, Vue } from 'vue-property-decorator';
import BaseHeader from '@/components/BaseHeader.vue';
import { ValidationObserver } from 'vee-validate';
import BaseFieldDateRange from '@/components/form/BaseFieldDateRange.vue';
import BaseFieldText from '@/components/form/BaseFieldText.vue';
import NewClaimYearSelector from '@/components/claims/new-claim/NewClaimYearSelector.vue';
import parse from 'date-fns/parse';
import { differenceInYears } from 'date-fns';

@Component({
  components: {
    NewClaimYearSelector,
    BaseHeader,
    ValidationObserver,
    BaseFieldDateRange,
    BaseFieldText,
  },
})
export default class NewClaimDetailForm extends Vue {
  get minDate() {
    const dateString =
      this.$store.getters['claim/agreement'].date_time_valid_from;
    const from = dateString
      ? parse(dateString, 'yyyy-MM-dd', new Date(Date.now()))
      : null;
    return from;
  }

  get maxDate() {
    const today = new Date(Date.now());
    const dateString =
      this.$store.getters['claim/agreement'].date_time_valid_to;
    const to = parse(dateString, 'yyyy-MM-dd', today);

    // if the agreement period is quarterly the max date should be the last day of the last quarter.
    const maxDate = this.isQuarterly
      ? new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, -1)
      : today;

    return maxDate < to ? maxDate : to;
  }

  get isAnnual(): boolean {
    const { claimPeriod } = this.getAgreementDuration();
    return claimPeriod === 'ANNUAL';
  }

  get isQuarterly(): boolean {
    const { claimPeriod } = this.getAgreementDuration();
    return claimPeriod === 'QUARTERLY';
  }

  get isMonthly(): boolean {
    const { claimPeriod } = this.getAgreementDuration();
    return claimPeriod === 'MONTHLY' || claimPeriod === null;
  }

  get isAgreementShorterOrEqualToOneYear(): boolean {
    const { dateTimeValidFrom, dateTimeValidTo } = this.getAgreementDuration();
    const from = parse(dateTimeValidFrom, 'yyyy-MM-dd', new Date(Date.now()));
    const to = parse(dateTimeValidTo, 'yyyy-MM-dd', new Date(Date.now()));
    return differenceInYears(to, from) <= 1;
  }

  get showMonthSelector(): boolean {
    return (
      this.isMonthly ||
      this.isQuarterly ||
      (this.isAnnual && this.isAgreementShorterOrEqualToOneYear)
    );
  }

  getAgreementDuration() {
    const {
      date_time_valid_from: dateTimeValidFrom,
      date_time_valid_to: dateTimeValidTo,
      claim_period: claimPeriod,
    } = this.$store.getters['claim/agreement'];

    return { dateTimeValidFrom, dateTimeValidTo, claimPeriod };
  }

  get dateRangeFormRules() {
    if (!this.isQuarterly) {
      return 'required';
    }

    const { dateTimeValidFrom, dateTimeValidTo } = this.getAgreementDuration();

    return `required|quarterly:${dateTimeValidFrom},${dateTimeValidTo}`;
  }

  mounted() {
    if (this.showMonthSelector && this.isAnnual) {
      const { dateTimeValidFrom, dateTimeValidTo } =
        this.getAgreementDuration();

      const from = parse(dateTimeValidFrom, 'yyyy-MM-dd', new Date(Date.now()));
      const to = parse(dateTimeValidTo, 'yyyy-MM-dd', new Date(Date.now()));

      const firstOfToMonth =
        to.getDate() !== 1 ? new Date(to.getFullYear(), to.getMonth(), 1) : to;

      this.$store.commit('claim/setNewClaimPeriod', [from, firstOfToMonth]);
    }
  }
}
